/**
 * Role View Toggle
 * UI component for admins to preview the app as different roles
 */

import React, { useState } from 'react';
import { Eye, EyeOff, ChevronDown, User, Shield } from 'lucide-react';
import { useRoleView } from '../../../contexts/RoleViewContext';
import { USER_ROLES, type UserRole } from '../../constants';

const ROLE_INFO = {
  [USER_ROLES.ADMIN]: {
    label: 'Admin',
    icon: Shield,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  [USER_ROLES.SENIOR_REVIEWER]: {
    label: 'Senior Reviewer',
    icon: Shield,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  [USER_ROLES.REVIEWER]: {
    label: 'Reviewer',
    icon: Shield,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  [USER_ROLES.USER]: {
    label: 'User',
    icon: User,
    color: 'text-neutral-600 dark:text-neutral-400',
    bgColor: 'bg-neutral-50 dark:bg-neutral-800',
    borderColor: 'border-neutral-200 dark:border-neutral-700',
  },
} as const;

export const RoleViewToggle: React.FC = () => {
  const { canImpersonate, isImpersonating, effectiveRole, actualRole, setViewAsRole, resetView } = useRoleView();
  const [isOpen, setIsOpen] = useState(false);

  // Don't show toggle if user is already a User (can't switch)
  if (!canImpersonate) {
    return null;
  }

  const currentRoleInfo = effectiveRole ? ROLE_INFO[effectiveRole] : null;
  const CurrentIcon = currentRoleInfo?.icon || User;

  // Only show actual role and User role
  const availableRoles: UserRole[] = [
    USER_ROLES.USER,
    actualRole!,
  ].filter((role, index, self) => self.indexOf(role) === index); // Remove duplicates if actualRole is User

  const handleRoleSelect = (role: UserRole) => {
    if (role === actualRole) {
      resetView();
    } else {
      setViewAsRole(role);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg border transition-all
          ${isImpersonating
            ? 'bg-warning-50 dark:bg-warning-900/20 border-warning-300 dark:border-warning-700'
            : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700'
          }
          hover:border-neutral-300 dark:hover:border-neutral-600
        `}
      >
        {isImpersonating ? (
          <Eye className="h-4 w-4 text-warning-600 dark:text-warning-400" />
        ) : (
          <EyeOff className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
        )}

        {currentRoleInfo && (
          <CurrentIcon className={`h-4 w-4 ${currentRoleInfo.color}`} />
        )}

        <span className="text-sm font-medium text-default-font dark:text-neutral-100">
          {isImpersonating ? 'Viewing as: ' : ''}
          {currentRoleInfo?.label || 'Select Role'}
        </span>

        <ChevronDown className={`h-4 w-4 text-neutral-500 dark:text-neutral-400 transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu - Opens upwards */}
          <div className="absolute bottom-full mb-2 left-0 z-20 min-w-[240px] bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
              <p className="text-xs font-medium text-subtext-color dark:text-neutral-400">
                Switch View:
              </p>
              <p className="text-xs text-subtext-color dark:text-neutral-500 mt-0.5">
                Your role: {ROLE_INFO[actualRole!]?.label}
              </p>
            </div>

            {/* Role Options */}
            <div className="py-1">
              {availableRoles.map((role) => {
                const roleInfo = ROLE_INFO[role];
                const RoleIcon = roleInfo.icon;
                const isActive = effectiveRole === role;
                const isActual = actualRole === role;

                return (
                  <button
                    key={role}
                    onClick={() => handleRoleSelect(role)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-left transition-colors
                      ${isActive
                        ? `${roleInfo.bgColor} ${roleInfo.color}`
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                      }
                    `}
                  >
                    <RoleIcon className={`h-4 w-4 ${isActive ? roleInfo.color : 'text-neutral-500 dark:text-neutral-400'}`} />

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${isActive ? roleInfo.color : 'text-default-font dark:text-neutral-100'}`}>
                          {roleInfo.label}
                        </span>
                        {isActual && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400">
                            You
                          </span>
                        )}
                        {isActive && !isActual && (
                          <Eye className="h-3.5 w-3.5 text-warning-600 dark:text-warning-400" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Reset Button */}
            {isImpersonating && (
              <>
                <div className="border-t border-neutral-200 dark:border-neutral-700" />
                <button
                  onClick={resetView}
                  className="w-full px-3 py-2 text-left text-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                >
                  Return to {ROLE_INFO[actualRole!]?.label} View
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Impersonation Warning Banner (when impersonating) */}
      {isImpersonating && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-warning-600 dark:text-warning-400 flex-shrink-0" />
            <p className="text-xs text-warning-700 dark:text-warning-300">
              You're viewing the app as <strong>{currentRoleInfo?.label}</strong>.
              Some features may be hidden.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
